import React, { useCallback } from "react";
import { GridApi } from "ag-grid-community";

export enum CheckboxStates {
  checked = "checked",
  unchecked = "unchecked",
  partial = "partial"
}

let lastUpdate = new Date();

export function useTableCheckbox(showPagination: boolean, gridApi: GridApi) {
  const [update, setUpdate] = React.useState(false);
  const [checkboxState, setCheckboxState] = React.useState<CheckboxStates>(
    CheckboxStates.unchecked
  );

  const updateCheckboxState = useCallback(() => {
    let currentUpdate = new Date();
    const sinceLastUpdateInSeconds = (currentUpdate.getTime() - lastUpdate.getTime()) / 1000;

    // performance optimization:
    // prevent performance issues since `onRowSelected` runs very qi\uickly for every row updated
    // do not update if last change was quicker then 100ms
    if (sinceLastUpdateInSeconds >= 0.1) {
      setUpdate((p) => !p);
      lastUpdate = new Date();
    }
  }, []);

  const getPaginationStatus = useCallback(() => {
    const currentPage = gridApi.paginationGetCurrentPage();
    const pageSize = gridApi.paginationGetPageSize();
    const rowsCount = gridApi.getDisplayedRowCount();

    const currentEndAt = (currentPage + 1) * pageSize;
    const startAt = currentPage * pageSize;
    const endAt = currentEndAt > rowsCount ? rowsCount : currentEndAt;

    return {
      currentEndAt,
      startAt,
      endAt,
      currentPage,
      pageSize,
      rowsCount
    };
  }, [gridApi]);

  function onCheckboxClick() {
    if (!gridApi) {
      return;
    }

    if (checkboxState === CheckboxStates.checked) {
      deselectAllCheckboxes();
      return;
    }
    selectAllCheckboxes();
  }

  function selectAllCheckboxes() {
    const { startAt, endAt } = getPaginationStatus();

    for (let i = startAt; i < endAt; i++) {
      const row = gridApi.getDisplayedRowAtIndex(i);
      row.setSelected(true);
    }
    updateCheckboxState();
  }

  function deselectAllCheckboxes() {
    const { startAt, endAt } = getPaginationStatus();

    for (let i = startAt; i < endAt; i++) {
      const row = gridApi.getDisplayedRowAtIndex(i);
      row.setSelected(false);
    }
    updateCheckboxState();
  }

  const getCheckboxState = useCallback(() => {
    if (!gridApi) {
      return undefined;
    }

    const { startAt, endAt } = getPaginationStatus();
    const state = [];

    for (let i = startAt; i < endAt; i++) {
      state.push(gridApi?.getDisplayedRowAtIndex(i).isSelected());
    }

    const allChecked = state.every((s) => s === true);
    const someChecked = !allChecked && state.some((s) => s === true);
    const noneChecked = state.every((s) => s === false);

    if (noneChecked) {
      return CheckboxStates.unchecked;
    }

    if (someChecked) {
      return CheckboxStates.partial;
    }

    return CheckboxStates.checked;
  }, [gridApi, getPaginationStatus]);

  React.useEffect(() => {
    if (showPagination) {
      setCheckboxState(getCheckboxState());
    }
  }, [update, showPagination, getCheckboxState]);

  return {
    checkboxState,
    onCheckboxClick,
    updateCheckboxState
  };
}
